import Link from "next/link";
import {CheckCircle, MessageSquare, User} from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {PostImage} from "@/components/ui/PostImage";
import type { Post } from "@/types/post";
import type { Category } from "@/types/category";
import {truncateHtml} from "@/lib/helper";

type Props = {
    posts: Post[];
    categories?: Category[];
    showCategory?: boolean;
};


export default function PostGrid({
                                     posts,
                                     categories = [],
                                     showCategory = false,
                                 }: Props) {
    const getPostStatus = (post: Post) => {
        const labels: Record<string, string> = {
            redirect: "ریردایرکت",
            "noindex,follow": "آرشیو شده",
            "410": "حذف شده",
        };

        if (post.published === 0) return "پیش‌نویس";

        return post.robots ? labels[post.robots] || null : null;
    };

    const getCategoryNames = (categoryIds?: string) => {
        if (!categoryIds) return "";

        return categoryIds
            .split(",")
            .map((id) => {
                const cat = categories.find((c) => c.id === parseInt(id));
                return cat ? cat.title : "";
            })
            .filter(Boolean)
            .join(" ، ");
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mb-2">
            {posts.map((post) => (
                <Card
                    key={post.id}
                    className="overflow-hidden relative flex flex-col border-transparent h-full hover:border-blue-600"
                >

                    <Link
                        className="w-full flex flex-col flex-1"
                        href={
                            post.published === 0
                                ? `/dashboard/posts/preview/${post.slug}`
                                : `/${process.env.NEXT_PUBLIC_BLOG_POST_SINGLE}/${post.slug}`
                        }
                        prefetch={true}
                    >


                        {getPostStatus(post) && (
                            <div className="absolute bg-red-500 text-2xl z-[5] px-16 py-0 top-[15px] right-[-65px] rotate-45">
                                <div className="text-white text-lg py-2">
                                    {getPostStatus(post)}
                                </div>
                            </div>
                        )}

                        <CardHeader className="p-3">
                            <PostImage
                                src={`${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_POST}/${post.primary_image?.split(",")[0] ?? ""}.${process.env.NEXT_PUBLIC_STORAGE_EXTENSION_PICTURE}?w=200`}
                                alt={post.title}
                                // priority={index < 2}
                            />
                        </CardHeader>

                        <CardContent className="p-4 flex flex-col flex-1">

                            <CardTitle className="text-lg font-bold mb-3 line-clamp-2">
                                {post.title}
                            </CardTitle>

                            <div
                                className="text-sm text-gray-600 text-justify line-clamp-3 mb-4"
                                dangerouslySetInnerHTML={{
                                    __html: truncateHtml(
                                        post.description ||
                                        "توضیحی برای این مقاله ثبت نشده است.",
                                        120
                                    ),
                                }}
                            />

                            <div className="mt-auto flex justify-between text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                  {post.author_id?.info?.name || "ناشناس"}
                              </span>

                                <span className="flex items-center gap-1">
                                <MessageSquare className="w-4 h-4" />
                                    {post.comment}
                              </span>
                            </div>

                            {post.features && post.features.length > 0 && (
                                <ul className="space-y-1 mb-4 flex-1">
                                    {post.features.slice(0, 2).map((f, i) => (
                                        <li key={i} className="text-sm flex items-center">
                                            <CheckCircle className="h-3 w-3 text-green-500 ml-2" />
                                            {f.title}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {showCategory && (
                                <div className={'w-full mt-3'}>
                                    <div className="text-xs text-gray-600 mb-2 rtl">
                                        <strong>دسته‌بندی: </strong>
                                        <span>{getCategoryNames(post.category_id)}</span>
                                    </div>
                                </div>
                            )}

                        </CardContent>

                    </Link>

                </Card>
            ))}
        </div>
    );
}